import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { Rating } from '@estee/elc-rating';
import { translate } from '@estee/elc-service';
import { ProductRatingViewController } from '~controllers/ProductRatingViewController';
import { IProduct } from '~interfaces/IRating';
import { classes } from '~utils/Classes';
import { PRODUCT_RATING } from '../../internal/constants/DataTestIds';
import { SocialProofReviewResponsiveWrapper } from '~views/SocialProofReviewResponsiveWrapper';

export interface ITranslation {
    readReviewsLabel: string;
    firstToWriteReviewLabel: string;
    reviewsNumberLabel: string;
    serviceReviewNumberSingular?: string;
}

export interface IProductRatingConfig {
    averageRating: number;
    className?: string;
    hideReadReviewsLink?: boolean;
    hideReviewsNumber?: boolean;
    hideReviewsLabel?: boolean;
    showReviewsNumberLabel?: boolean;
    showEmptyRatingsStars: boolean;
    numberOfStars: number;
    productId: string;
    reviewsNumberLabel?: string;
    productRating?: number;
    reviewsNumber?: number;
    isClickable: boolean;
    product: IProduct;
    socialProofReviews: ISocialProofReviewConfig;
}

interface ISocialProofReviewMaxNumberOfCharacters {
    longHeadline: number;
    shortHeadline: number;
}

export interface ISocialProofReviewConfig {
    enabled: boolean;
    minNumberOfStars: number;
    maxNumberOfCharacters: ISocialProofReviewMaxNumberOfCharacters;
}

export interface IProductRating {
    translations?: ITranslation;
    onReadReviewsClick?(): void;
    config: IProductRatingConfig;
    viewController: ProductRatingViewController;
}

const RatingWrapper = styled.div`
    width: 100%;
    justify-content: flex-end;
    align-items: center;
    display: flex;
`;

const ratingWrapperClassnames = classes('product-rating-wrapper');

@translate(['readReviewsLabel', 'firstToWriteReviewLabel', 'reviewsNumberLabel', 'serviceReviewNumberSingular'])
@observer
export class ProductRating extends React.Component<IProductRating> {
    private productRatingViewController: ProductRatingViewController;

    constructor(props: IProductRating) {
        super(props);
        this.productRatingViewController = props.viewController;
    }

    public async componentDidMount() {
        const {
            productId,
            numberOfStars,
            productRating,
            reviewsNumber,
            product,
            socialProofReviews
        } = this.props.config || {};

        await this.productRatingViewController.loaded({
            productId,
            numberOfStars,
            productRating,
            reviewsNumber,
            product,
            socialProofReviews
        });
    }

    private readReviewsClickHandler = async () => {
        if (this.props.onReadReviewsClick) {
            this.props.onReadReviewsClick();
        } else {
            this.productRatingViewController.onReadReviewsClick();
        }
    };

    public render() {
        const { readReviewsLabel, firstToWriteReviewLabel, reviewsNumberLabel, serviceReviewNumberSingular } = this.props
            .translations as ITranslation;
        const {
            className,
            numberOfStars,
            hideReviewsLabel,
            showReviewsNumberLabel,
            hideReviewsNumber,
            hideReadReviewsLink,
            showEmptyRatingsStars,
            isClickable
        } = this.props.config;

        if (!this.productRatingViewController.data.loaded) {
            return null;
        }

        const {
            data: {
                onWriteReviewClick,
                numberOfReviews,
                percentageRating,
                averageRating,
                filteredSocialProofReview
            }
        } = this.productRatingViewController;

        const reviewsLabel = numberOfReviews === 1 && !!serviceReviewNumberSingular ? serviceReviewNumberSingular : reviewsNumberLabel;

        return percentageRating !== undefined ? (
            <RatingWrapper className={ratingWrapperClassnames} data-test-id={PRODUCT_RATING}>
                {filteredSocialProofReview && (
                    <SocialProofReviewResponsiveWrapper
                        desktopHeadline={filteredSocialProofReview.desktopHeadline}
                        mobileHeadline={filteredSocialProofReview.mobileHeadline}
                    />
                )}
                <Rating
                    className={className}
                    numberOfStars={numberOfStars}
                    readReviewsLabel={readReviewsLabel}
                    firstToWriteReviewLabel={firstToWriteReviewLabel}
                    showReviewsNumber={!hideReviewsNumber}
                    showReviewsNumberLabel={showReviewsNumberLabel}
                    showReadReviews={!hideReadReviewsLink}
                    hideReviewsLabel={hideReviewsLabel}
                    showEmptyStars={showEmptyRatingsStars}
                    onWriteReviewsClick={onWriteReviewClick}
                    onReadReviewsClick={this.readReviewsClickHandler}
                    percentageRating={percentageRating}
                    reviewsNumber={numberOfReviews}
                    averageRating={averageRating}
                    isClickable={isClickable}
                    reviewsNumberLabel={reviewsLabel}
                />
            </RatingWrapper>
        ) : null;
    }
}
// tslint:disable-next-line:no-default-export
export default ProductRating;
